import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import {
  COMPARISON_FEATURES,
  formatComparisonCell,
  formatPricingVerifiedDate,
  getCompetitorBySlug,
  isCompetitorSlug,
} from '@/lib/competitors';
import {
  buildHarborSoftwareApplicationJsonLd,
  HARBOR_FACTS,
  harborAnnualPriceLabel,
  harborMonthlyPriceLabel,
} from '@/lib/harbor-facts';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';
import shipSvg from '@/assets/ship.svg';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function UnknownCompetitorPage({ competitorSlug }: { competitorSlug: string }) {
  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Comparison not found | Harbor'
        description='This comparison page is not available.'
        path={`/compare/${competitorSlug}`}
        noindex
      />
      <main className='mx-auto max-w-3xl px-6 py-24 text-center'>
        <h1 className='text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Comparison not found
        </h1>
        <p className='mt-4 text-lg text-gray-600' style={nunito}>
          This competitor page is not available. Browse the full comparison list instead.
        </p>
        <Link
          to='/compare'
          className='mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-hover'
          style={{ ...nunito, fontWeight: 600 }}
        >
          <FiArrowLeft />
          Back to all comparisons
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function CompetitorPage() {
  const { competitor: competitorSlug } = Route.useParams();
  const competitor = isCompetitorSlug(competitorSlug)
    ? getCompetitorBySlug(competitorSlug)
    : null;

  useEffect(() => {
    if (competitor) {
      track('Compare Competitor View', { competitor: competitor.slug });
    }
  }, [competitor]);

  if (!competitor) {
    return <UnknownCompetitorPage competitorSlug={competitorSlug} />;
  }

  const path = `/compare/${competitor.slug}`;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: competitor.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://harborcoparent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Compare',
        item: 'https://harborcoparent.com/compare',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `Harbor vs ${competitor.name}`,
        item: `https://harborcoparent.com/compare/${competitor.slug}`,
      },
    ],
  };

  const competitorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: competitor.name,
    description: competitor.tagline,
    url: competitor.pricingSourceUrl,
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={
          competitor.seoTitle ??
          `Harbor vs ${competitor.name}: Pricing, Features & Best Co-Parenting App in 2026`
        }
        description={
          competitor.seoDescription ??
          `Compare Harbor vs ${competitor.name} on pricing, core features, and day-to-day co-parent workflows.`
        }
        path={path}
        jsonLd={[faqJsonLd, breadcrumbJsonLd, buildHarborSoftwareApplicationJsonLd(), competitorSchema]}
      />

      <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
        <nav className='mx-auto flex max-w-7xl items-center justify-between px-6 py-4'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <div className='flex items-center'>
              <img src={shipSvg} alt='' className='w-5 h-5 mr-3' />
              <span className='flex items-center mt-2'>Harbor</span>
            </div>
          </Link>
          <Link
            to='/compare'
            className='flex items-center gap-2 font-medium text-white/90 transition-colors hover:text-white'
            style={{ ...nunito, fontWeight: 400 }}
          >
            <FiArrowLeft />
            Back to Compare
          </Link>
        </nav>
      </header>

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-5xl px-6'>
          <h1 className='text-4xl font-bold text-gray-900 md:text-5xl' style={{ ...nunito, fontWeight: 700 }}>
            Harbor vs {competitor.name}
          </h1>
          <p className='mt-5 max-w-4xl text-lg leading-relaxed text-gray-700 md:text-xl' style={nunito}>
            {competitor.vsHarbor}
          </p>
        </div>
      </section>

      <section className='mx-auto grid max-w-5xl gap-6 px-6 py-12 md:grid-cols-2'>
        <article className='rounded-xl border border-emerald-200 bg-emerald-50/40 p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Harbor at a glance
          </h2>
          <ul className='mt-4 space-y-2 text-gray-700' style={nunito}>
            <li>
              {harborMonthlyPriceLabel()} or {harborAnnualPriceLabel()}
            </li>
            <li>Subscription is $9.99/month or $49.99/year per person</li>
            <li>Messaging, expenses, custody calendar, Temperature Check, and exports</li>
            <li>Works solo or with both parents · {HARBOR_FACTS.platforms}</li>
          </ul>
        </article>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            {competitor.name} at a glance
          </h2>
          <ul className='mt-4 space-y-2 text-gray-700' style={nunito}>
            <li>{competitor.pricingMonthly}</li>
            <li>{competitor.pricingAnnual}</li>
            <li>{competitor.tagline}</li>
          </ul>
          <p className='mt-4 text-sm text-gray-500' style={nunito}>
            Pricing from{' '}
            <a
              href={competitor.pricingSourceUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='font-semibold text-primary underline'
            >
              {competitor.name} pricing
            </a>
            , last checked {formatPricingVerifiedDate(competitor.pricingVerifiedDate)}.
          </p>
        </article>
      </section>

      <section className='mx-auto max-w-5xl px-6 pb-12'>
        <h2 className='mb-5 text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
          Feature comparison
        </h2>
        <div className='overflow-x-auto rounded-2xl border border-gray-200'>
          <table className='min-w-full border-collapse bg-white text-left' style={nunito}>
            <caption className='sr-only'>Harbor and competitor feature matrix</caption>
            <thead className='bg-gray-50'>
              <tr>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Feature
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Harbor
                </th>
                <th scope='col' className='border-b border-gray-200 p-4 font-semibold text-gray-900'>
                  {competitor.name}
                </th>
              </tr>
            </thead>
            <tbody className='text-sm text-gray-700'>
              {COMPARISON_FEATURES.map((feature) => (
                <tr key={feature.key}>
                  <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                    {feature.label}
                  </th>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {formatComparisonCell(feature.harborValue)}
                  </td>
                  <td
                    className={
                      feature.key === 'householdPrice' && competitor.highlightPrice
                        ? 'border-b border-gray-200 p-4 font-extrabold text-red-600'
                        : 'border-b border-gray-200 p-4'
                    }
                  >
                    {feature.key === 'householdPrice' && competitor.highlightPrice ? (
                      <strong className='font-extrabold text-red-600'>
                        {formatComparisonCell(competitor.featureMatrix[feature.key])}
                      </strong>
                    ) : (
                      formatComparisonCell(competitor.featureMatrix[feature.key])
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className='mx-auto grid max-w-5xl gap-6 px-6 pb-12 md:grid-cols-2'>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            When to pick {competitor.name}
          </h2>
          <p className='mt-4 text-gray-700' style={nunito}>
            {competitor.whenToPickThem}
          </p>
          <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700' style={nunito}>
            {competitor.pros.map((pro) => (
              <li key={pro}>{pro}</li>
            ))}
          </ul>
        </article>
        <article className='rounded-xl border border-gray-200 bg-white p-6'>
          <h2 className='text-xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Where Harbor is usually stronger
          </h2>
          <p className='mt-4 text-gray-700' style={nunito}>
            {competitor.verdict}
          </p>
          <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700' style={nunito}>
            {competitor.cons.map((con) => (
              <li key={con}>{con}</li>
            ))}
          </ul>
        </article>
      </section>

      <section className='mx-auto max-w-5xl px-6 pb-12'>
        <h2 className='mb-5 text-2xl font-bold text-gray-900 md:text-3xl' style={{ ...nunito, fontWeight: 700 }}>
          Frequently asked questions
        </h2>
        <div className='space-y-4'>
          {competitor.faqs.map((faq) => (
            <article key={faq.question} className='rounded-xl border border-gray-200 bg-white p-5'>
              <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                {faq.question}
              </h3>
              <p className='mt-2 text-gray-700' style={nunito}>
                {faq.answer}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 pb-12'>
        <p className='text-sm text-gray-600' style={nunito}>
          Related Harbor pages:{' '}
          <Link to='/features' className='font-semibold text-primary underline'>
            Features
          </Link>
          {', '}
          <Link to='/court-order-language' className='font-semibold text-primary underline'>
            court-order language
          </Link>
          {', and '}
          <Link
            to='/parenting-plan'
            className='font-semibold text-primary underline'
          >
            free parenting-plan builder
          </Link>
          {', and '}
          <Link
            to='/coparenting-app-cost-calculator'
            className='font-semibold text-primary underline'
          >
            the co-parenting app cost calculator
          </Link>
          .
        </p>
      </section>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white' style={{ ...nunito, fontWeight: 700 }}>
            Try Harbor
          </h2>
          <p className='mx-auto mt-4 max-w-xl text-lg text-white/85' style={nunito}>
            {HARBOR_FACTS.ctaQuote}
          </p>
          <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
            <a
              href={getAppStoreUrl(`compare-${competitor.slug}`, getUtmParamsFromUrl())}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                track('Click App Store', {
                  location: `Compare ${competitor.slug} CTA`,
                  competitor: competitor.slug,
                })
              }
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FaApple size={18} />
              App Store
            </a>
            <a
              href={getPlayStoreUrl(`compare-${competitor.slug}`, getUtmParamsFromUrl())}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                track('Click Google Play', {
                  location: `Compare ${competitor.slug} CTA`,
                  competitor: competitor.slug,
                })
              }
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FaGooglePlay size={18} />
              Google Play
            </a>
            <Link
              to='/auth/sign-up'
              onClick={() =>
                track('Click Sign Up', {
                  location: `Compare ${competitor.slug} CTA`,
                  competitor: competitor.slug,
                })
              }
              className='inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
              style={{ ...nunito, fontWeight: 600 }}
            >
              Sign up on web
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/compare/$competitor')({
  component: CompetitorPage,
});
